'use client';

import classnames from 'classnames';
import { useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useState } from 'react';

import SectionTitle from '@/app/[locale]/(default-layout)/_components/SectionTitle';

import { FeaturedContentData } from '@/helpers/page-api-helper';

import useHorizontalSwipeRecognizer from '@/hooks/useHorizontalSwipeRecognizer';

import ButtonWithIcon, { ButtonWithIconStyle } from '@cineamo/ui/src/button/ButtonWithIcon';
import { IconBack24 } from '@cineamo/ui/src/icons/ic-back';
import { IconForth24 } from '@cineamo/ui/src/icons/ic-forth';

import CenterLayout from '@cineamo/composables/src/CenterLayout';

import FeaturedContentItem from './FeaturedContentItem';

type FeaturedContentTabPanelProps = {
    featured: FeaturedContentData[];
};

const FeaturedContentTabPanel: React.FC<FeaturedContentTabPanelProps> = ({ featured }) => {
    const t = useTranslations();

    const [currentIndex, setCurrentIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);

    const goToNext = useCallback(() => {
        setCurrentIndex((prev) => (prev + 1) % featured.length);
    }, [featured.length]);

    const goToPrev = useCallback(() => {
        setCurrentIndex((prev) => (prev - 1 + featured.length) % featured.length);
    }, [featured.length]);

    const { ref: swipeRef } = useHorizontalSwipeRecognizer({
        onLeftSwipe: goToNext,
        onRightSwipe: goToPrev
    });

    // Auto-advance every 6 seconds, pause on hover
    useEffect(() => {
        if (featured.length <= 1 || isHovered) return;
        const interval = setInterval(goToNext, 6000);
        return () => clearInterval(interval);
    }, [featured.length, goToNext, isHovered]);

    if (featured.length === 0) {
        return null;
    }

    const safeIndex = currentIndex < featured.length ? currentIndex : 0;
    const currentItem = featured[safeIndex];

    return (
        <CenterLayout className="space-y-12 lg:space-y-16">
            <SectionTitle title={t('home.featured')} />

            <div
                className="relative"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}>
                <div ref={swipeRef as React.RefObject<HTMLDivElement>}>
                    <FeaturedContentItem
                        key={`FeaturedContentItem#${currentItem.id}`}
                        content={currentItem}
                    />
                </div>

                {/* Outside arrows — desktop only (swipe on mobile) */}
                {featured.length > 1 && (
                    <div className="hidden lg:flex absolute top-0 w-full h-full pointer-events-none items-center justify-between">
                        <ButtonWithIcon
                            className="pointer-events-auto -translate-x-48"
                            icon={<IconBack24 />}
                            onClick={goToPrev}
                            style={ButtonWithIconStyle.transparent}
                            accessibilityName={t('common.previous')}
                        />
                        <ButtonWithIcon
                            className="pointer-events-auto translate-x-48 ml-auto"
                            icon={<IconForth24 />}
                            onClick={goToNext}
                            style={ButtonWithIconStyle.transparent}
                            accessibilityName={t('common.next')}
                        />
                    </div>
                )}
            </div>

            {/* Dot indicators */}
            {featured.length > 1 && (
                <div className="flex items-center justify-center gap-8">
                    {featured.map((item, index) => (
                        <button
                            key={`FeaturedDot#${item.id}`}
                            className={classnames(
                                'rounded-full transition-all duration-300 size-8',
                                index === safeIndex
                                    ? 'bg-[var(--primary-color)]'
                                    : 'bg-[rgba(var(--surface-color-rgb),0.3)] hover:bg-[rgba(var(--surface-color-rgb),0.5)]'
                            )}
                            type="button"
                            onClick={() => setCurrentIndex(index)}
                            aria-label={`${t('home.featured')} ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </CenterLayout>
    );
};

export default FeaturedContentTabPanel;
